import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import type { Arrangement } from '../../model/arrangement'
import type { CompositionNode, CompositionRegion, RegionRef } from '../../model/composition'
import { samePath, type LinkPath } from '../../model/link'
import { DragHandle } from '../../ui/DragHandle'
import { PinChip } from '../../ui/PinChip'
import { RowMenu } from '../../ui/RowMenu'
import { moved, type DragRow, type Reorder } from '../../ui/reorder'

/**
 * A document's composition, top to bottom, with a control on every node
 * that declares one.
 *
 * **A list, deliberately.** `children` is an ordered list and the type's
 * fields arrange it by link id, so a linear rendering is the one view
 * that cannot misrepresent what is stored. Nesting is indentation, not a
 * drawn layout: a Grid's cells appear in cell order with their spans
 * shown as values in the editor, because arrangement is the part a theme
 * interprets and the admin has no standing to draw it.
 *
 * **What changed is that it recurses.** Every inline node draws its own
 * regions with its own controls, because a Container inside a page
 * arranges children exactly as the page does and the only difference is
 * which intents do it. Before, the tree was drawn to any depth and
 * editable at one, so an inline Container was a row an author could
 * select and never fill.
 *
 * A linked child stays a leaf. Its contents live in another stream and
 * this document does not hold them — the way in is to open it, which the
 * editor panel offers once it is selected.
 *
 * **A block is dragged to move it.** Every `arrange` intent carries the
 * whole order of one region, so a drop is one command and one revision —
 * the up and down arrows were the worse control over the same command,
 * and they are still in each row's menu for anyone not using a pointer.
 */
export interface Arranging {
  place?: { label: string }
  act?: { label: string }
  arrange?: { label: string }
  /** Present where the holder can carry a placement between its regions. */
  move?: { label: string }
}

/**
 * What a node can have done to it, resolved per node and per region.
 *
 * A function rather than a value because the answer differs down the
 * tree: a Page's regions are served by `page.block_placed` and a
 * Container's by `container.item_placed`, and both appear on this screen
 * at once. The caller resolves it from the manifest; nothing here knows a
 * type name.
 */
export type ArrangingFor = (node: CompositionNode, region: CompositionRegion) => Arranging

export interface CompositionActions {
  onSelect: (path: LinkPath) => void
  onAdd: (node: CompositionNode, region: CompositionRegion) => void
  /**
   * One region, rearranged: the whole order, which is what the intent
   * takes. A drop and a menu step produce the same array, so there is
   * one callback rather than one per gesture.
   */
  onReorder: (node: CompositionNode, region: CompositionRegion, order: string[]) => void
  /**
   * One placement carried to the end of another of its holder's regions:
   * what a drop across regions sends, from the row's menu.
   */
  onMove: (node: CompositionNode, from: CompositionRegion, to: CompositionRegion, placement: CompositionNode) => void
  onAct: (node: CompositionNode, region: CompositionRegion, placement: CompositionNode) => void
  onPin: (placement: CompositionNode) => void
}

export function Composition({
  root,
  arrangingFor,
  titleOf,
  dragging,
  groupOf,
  renderTarget,
  selection,
  actions,
  hides,
  busy,
}: {
  root: CompositionNode
  arrangingFor: ArrangingFor
  /**
   * What a type is called, in words.
   *
   * Passed in rather than read here, on the same terms `arrangingFor`
   * is: the manifest is the caller's, and this control knows no type
   * name and no prose of its own.
   */
  titleOf: (typeName: string) => string
  /**
   * The drag, owned by the caller and shared by every region on screen.
   *
   * One hook rather than one per region, because a block dragged from
   * the header into the body is a drop only something watching both ends
   * can see. Whether that drop is *allowed* is the caller's answer too:
   * it depends on an intent the manifest declares.
   */
  dragging: Reorder<RegionRef>
  /** How two regions are told apart. */
  groupOf: (node: CompositionNode, region: CompositionRegion) => string
  /**
   * What a linked placement points at, in words.
   *
   * A render prop on the same terms `titleOf` is a function: the name
   * lives in the target's own stream, and reading it is the caller's
   * business rather than this control's. Absent, a linked row says its
   * target's id, which is what it always said.
   */
  renderTarget?: (placement: CompositionNode) => React.ReactNode
  selection: LinkPath
  actions: CompositionActions
  /**
   * Arrangements the surrounding screen draws itself, by property name.
   *
   * The site editor draws a website's routing tree and its 404, so this
   * control must not also report them as things nobody draws — an author
   * looking at a routing editor being told routing is not drawn is worse
   * than silence.
   */
  hides?: string[]
  busy: boolean
}) {
  const undrawn = root.undrawn.filter((arrangement) => !hides?.includes(arrangement.property))
  return (
    <Stack spacing={2}>
      {root.regions.map((region) => (
        <Paper key={region.slot ?? '(unnamed)'}>
          <RegionHeader
            node={root}
            region={region}
            arranging={arrangingFor(root, region)}
            titleOf={titleOf}
            actions={actions}
            busy={busy}
          />
          <RegionBody
            node={root}
            region={region}
            arrangingFor={arrangingFor}
            titleOf={titleOf}
            dragging={dragging}
            groupOf={groupOf}
            renderTarget={renderTarget}
            selection={selection}
            actions={actions}
            busy={busy}
          />
        </Paper>
      ))}

      <Undrawn arrangements={undrawn} />

      {root.unarranged.length > 0 && (
        <Paper>
          <Stack sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h3">Unarranged</Typography>
            <Typography variant="caption" color="text.secondary">
              Children of this component that no arrangement names — not the ones drawn here, and not the ones below
              that are not drawn. They compose nowhere.
            </Typography>
          </Stack>
          {root.unarranged.map((placement) => (
            <PlacementRow
              key={placement.linkId}
              placement={placement}
              title={titleOf(placement.type)}
              target={renderTarget?.(placement)}
              selected={samePath(selection, placement.path)}
              onSelect={actions.onSelect}
              onPin={actions.onPin}
            />
          ))}
        </Paper>
      )}
    </Stack>
  )
}

/**
 * A region's heading and its Add button.
 *
 * The heading is the region's name, or the type's own when it keeps one
 * unnamed list — "Items" would be this client inventing a word for
 * something the type spells `items` and a theme never shows.
 */
function RegionHeader({
  node,
  region,
  arranging,
  titleOf,
  actions,
  busy,
}: {
  node: CompositionNode
  region: CompositionRegion
  arranging: Arranging
  titleOf: (typeName: string) => string
  actions: CompositionActions
  busy: boolean
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}
    >
      <Typography variant="h3">{region.slot ?? titleOf(node.type)}</Typography>
      {!region.declared && (
        <Tooltip title="Stored here but not declared by this type. Nothing refuses it — the vocabulary is advisory — but no theme is guaranteed to render it.">
          <Chip size="small" color="warning" variant="outlined" label="undeclared" />
        </Tooltip>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {arranging.place && (
        <Button size="small" startIcon={<AddIcon />} disabled={busy} onClick={() => actions.onAdd(node, region)}>
          {arranging.place.label}
        </Button>
      )}
    </Stack>
  )
}

function RegionBody({
  node,
  region,
  arrangingFor,
  titleOf,
  dragging,
  groupOf,
  renderTarget,
  selection,
  actions,
  busy,
}: {
  node: CompositionNode
  region: CompositionRegion
  arrangingFor: ArrangingFor
  titleOf: (typeName: string) => string
  dragging: Reorder<RegionRef>
  groupOf: (node: CompositionNode, region: CompositionRegion) => string
  renderTarget?: (placement: CompositionNode) => React.ReactNode
  selection: LinkPath
  actions: CompositionActions
  busy: boolean
}) {
  const group = groupOf(node, region)
  const ref: RegionRef = { node, region }
  const arranging = arrangingFor(node, region)
  // The stored order, dangling entries included, because that is what
  // the intent must send back: an order that dropped an entry the
  // arrangement still names would silently leave it at the end.
  const order = region.linkIds

  // An empty region is exactly the one an author is most likely to be
  // dragging into, and it has no row to aim at — so the container takes
  // the drop.
  if (region.placements.length === 0 && region.dangling.length === 0) {
    return (
      <Typography
        {...dragging.zoneProps(group, order, ref)}
        variant="body2"
        color="text.secondary"
        sx={{
          px: 2,
          py: 2,
          bgcolor: dragging.landingIn(group) ? 'action.hover' : undefined,
          outline: dragging.landingIn(group) ? 2 : 0,
          outlineColor: 'primary.main',
        }}
      >
        Empty.
      </Typography>
    )
  }

  // Where a placement here can be carried without a pointer: the holder's
  // other named regions that the same move serves, which are exactly the
  // ones a drag from this region may drop into.
  const destinations = arranging.move
    ? node.regions.filter((other) => other !== region && other.keyed && arrangingFor(node, other).move)
    : []

  const step = (linkId: string, direction: -1 | 1) => {
    const from = order.indexOf(linkId)
    const target = order[from + direction]
    if (from < 0 || target === undefined) return
    const next = moved(order, linkId, target)
    if (next) actions.onReorder(node, region, next)
  }

  return (
    <Box {...dragging.zoneProps(group, order, ref)}>
      {region.placements.map((placement, index) => {
        const linkId = placement.linkId!
        // Draggable where the region can be rearranged *or* the node can
        // move a block out of it. A region of one in a type that
        // declares a move is still worth picking up.
        const movable = Boolean(arranging.arrange) || Boolean(arranging.move)
        const at = order.indexOf(linkId)
        return (
          <Box key={placement.linkId}>
            {index > 0 && <Divider />}
            <PlacementRow
              placement={placement}
              title={titleOf(placement.type)}
              target={renderTarget?.(placement)}
              selected={samePath(selection, placement.path)}
              onSelect={actions.onSelect}
              onPin={actions.onPin}
              drag={movable ? dragging.rowProps(group, linkId, order, ref) : undefined}
              held={movable && dragging.isHeld(group, linkId)}
              landing={movable ? dragging.landingOn(group, linkId) : undefined}
              handle={
                movable && (
                  <DragHandle
                    label={arranging.arrange?.label ?? arranging.move?.label ?? 'Move'}
                    disabled={busy}
                  />
                )
              }
              menu={
                <RowMenu
                  disabled={busy}
                  actions={[
                    ...(arranging.arrange
                      ? [
                          {
                            label: 'Move up',
                            icon: <ArrowUpwardIcon fontSize="small" />,
                            disabled: at <= 0,
                            detail: arranging.arrange.label,
                            onSelect: () => step(linkId, -1),
                          },
                          {
                            label: 'Move down',
                            icon: <ArrowDownwardIcon fontSize="small" />,
                            disabled: at < 0 || at >= order.length - 1,
                            detail: arranging.arrange.label,
                            onSelect: () => step(linkId, 1),
                          },
                        ]
                      : []),
                    ...destinations.map((other) => ({
                      label: `Move to the ${other.slot}`,
                      icon: <SwapVertIcon fontSize="small" />,
                      detail: arranging.move?.label,
                      onSelect: () => actions.onMove(node, region, other, placement),
                    })),
                    ...(placement.targetId
                      ? [
                          {
                            label: placement.pin?.kind === 'pinned' ? 'Change or release the pin' : 'Pin to a revision',
                            icon: <PushPinOutlinedIcon fontSize="small" />,
                            detail: 'Whether this placement follows the component it links to, or holds one revision.',
                            onSelect: () => actions.onPin(placement),
                          },
                        ]
                      : []),
                    ...(arranging.act
                      ? [
                          {
                            label: arranging.act.label,
                            icon: <DeleteOutlineIcon fontSize="small" />,
                            destructive: true,
                            detail: 'This arranges the placement — the component it links to is untouched.',
                            onSelect: () => actions.onAct(node, region, placement),
                          },
                        ]
                      : []),
                  ]}
                />
              }
            />

            {/* The placement's own composition, on the same terms. An
                inline node arranges children exactly as its holder does,
                so it gets the same controls rather than a read-only
                rendering of what it contains. */}
            <Nested
              node={placement}
              arrangingFor={arrangingFor}
              titleOf={titleOf}
              dragging={dragging}
              groupOf={groupOf}
              renderTarget={renderTarget}
              selection={selection}
              actions={actions}
              busy={busy}
            />
          </Box>
        )
      })}

      {region.dangling.map((linkId) => (
        <Stack key={linkId} direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.5 }}>
          <WarningAmberIcon fontSize="small" color="error" />
          <Typography variant="body2">
            This region names <code>{linkId}</code>, and the document has no such child.
          </Typography>
        </Stack>
      ))}
    </Box>
  )
}

/**
 * An inline placement's own regions, indented under it.
 *
 * Each region gets its heading and its Add button here too, because a
 * node with two regions and no headings is a list an author cannot tell
 * apart — and because the Add button is the only thing that says a region
 * exists while it is empty.
 */
function Nested({
  node,
  arrangingFor,
  titleOf,
  dragging,
  groupOf,
  renderTarget,
  selection,
  actions,
  busy,
}: {
  node: CompositionNode
  arrangingFor: ArrangingFor
  titleOf: (typeName: string) => string
  dragging: Reorder<RegionRef>
  groupOf: (node: CompositionNode, region: CompositionRegion) => string
  renderTarget?: (placement: CompositionNode) => React.ReactNode
  selection: LinkPath
  actions: CompositionActions
  busy: boolean
}) {
  if (node.linked) return null
  if (node.regions.length === 0 && node.unarranged.length === 0 && node.undrawn.length === 0) return null

  return (
    <Box sx={{ pl: 2 + node.depth * 2, borderLeft: 2, borderColor: 'divider', ml: 2 }}>
      {node.regions.map((region) => {
        const arranging = arrangingFor(node, region)
        return (
          <Box key={region.slot ?? '(unnamed)'}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 0.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {region.slot ?? titleOf(node.type).toLowerCase()}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {arranging.place && (
                <Button size="small" startIcon={<AddIcon />} disabled={busy} onClick={() => actions.onAdd(node, region)}>
                  {arranging.place.label}
                </Button>
              )}
            </Stack>
            <RegionBody
              node={node}
              region={region}
              arrangingFor={arrangingFor}
              titleOf={titleOf}
              dragging={dragging}
              groupOf={groupOf}
              renderTarget={renderTarget}
              selection={selection}
              actions={actions}
              busy={busy}
            />
          </Box>
        )
      })}

      <Undrawn arrangements={node.undrawn} />

      {node.unarranged.map((placement) => (
        <PlacementRow
          key={placement.linkId}
          placement={placement}
          title={titleOf(placement.type)}
          target={renderTarget?.(placement)}
          selected={samePath(selection, placement.path)}
          onSelect={actions.onSelect}
          onPin={actions.onPin}
        />
      ))}
    </Box>
  )
}

/**
 * Arrangements this client cannot draw, stated rather than left out.
 *
 * A component whose placements are all in one of these would otherwise
 * render as empty, which is the one wrong thing to say about it.
 */
function Undrawn({ arrangements }: { arrangements: Arrangement[] }) {
  if (arrangements.length === 0) return null
  return (
    <Stack spacing={0.5} sx={{ px: 2, py: 1.5 }}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
        <WarningAmberIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        {arrangements.map((arrangement) => (
          <Chip key={arrangement.property} size="small" variant="outlined" label={arrangement.property} />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        This component arranges placements here too, in a shape that is not an ordered list — so it is not drawn as one.
        Nothing is hidden: a placement in one of these is still a child, and appears under Unarranged if no region names
        it.
      </Typography>
    </Stack>
  )
}

function PlacementRow({
  placement,
  title,
  target,
  selected,
  onSelect,
  onPin,
  drag,
  held,
  landing,
  handle,
  menu,
}: {
  placement: CompositionNode
  /** What this node's type is called, in words. */
  title: string
  /**
   * What it links to, drawn by the caller. Absent on an inline node,
   * which links to nothing, and on a caller that has no reading of it.
   */
  target?: React.ReactNode
  selected: boolean
  onSelect: (path: LinkPath) => void
  onPin: (placement: CompositionNode) => void
  /** Absent where nothing arranges this row — the unarranged list, and a region of one. */
  drag?: DragRow
  held?: boolean
  landing?: 'above' | 'below'
  handle?: React.ReactNode
  menu?: React.ReactNode
}) {
  return (
    <Stack
      {...drag}
      direction="row"
      spacing={1}
      alignItems="center"
      onClick={() => onSelect(placement.path)}
      sx={{
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        opacity: held ? 0.4 : 1,
        borderTop: landing === 'above' ? 2 : 0,
        borderBottom: landing === 'below' ? 2 : 0,
        borderColor: 'primary.main',
        bgcolor: selected ? 'action.selected' : undefined,
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      {handle}
      <Tooltip title="The placement's link id. Selection is a path of these from the root — the same component linked twice is two placements.">
        <Chip size="small" label={placement.linkId} />
      </Tooltip>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      {/* What it points at, named by whoever can read that stream —
          this control cannot, and a row printing a uuid says the least
          useful true thing available. The id is on the hover instead. */}
      {placement.targetId &&
        (target ?? (
          <Typography variant="body2" color="text.secondary" noWrap>
            → {placement.targetId}
          </Typography>
        ))}
      <Box sx={{ flexGrow: 1 }} />
      <PinChip pin={placement.pin} onClick={() => onPin(placement)} />
      {menu}
    </Stack>
  )
}
